import { useEffect, useRef } from 'react'
import { useTranslation } from '../../i18n'
import { COLORS } from '../../lib/theme'
import { Icon } from '../Icon'

export interface ReplayChooserOption {
  value: number
  /** The number, shown large. */
  top: string
  /** The unit beneath it, e.g. "MIN". Omitted when the value speaks for itself. */
  bottom?: string
}

export interface ReplayChooserPopoverProps {
  title: string
  options: ReplayChooserOption[]
  selected: number
  onSelect: (value: number) => void
  onClose: () => void
}

/**
 * The "Skip by" / speed chooser: a card anchored above its chip holding one
 * segmented row of options.
 *
 * A popover rather than a bottom sheet, because the map behind it is the thing being
 * configured — a sheet with a scrim would black out the hunt while you pick a speed
 * to watch it at.
 */
export function ReplayChooserPopover({
  title,
  options,
  selected,
  onSelect,
  onClose,
}: ReplayChooserPopoverProps) {
  const { t } = useTranslation('replay')
  const ref = useRef<HTMLDivElement>(null)

  // A click anywhere else dismisses. Registered on pointerdown so the popover is
  // gone before the click lands on whatever was underneath it.
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose
  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      const element = ref.current
      if (element === null) return
      const target = event.target as Node | null
      // The chip that opened this is the popover's sibling, so a press on it must
      // fall through to its own toggle rather than being treated as "outside".
      if (target !== null && (element.contains(target) || element.parentElement?.contains(target))) {
        return
      }
      onCloseRef.current()
    }
    window.addEventListener('pointerdown', onPointerDown)
    return () => window.removeEventListener('pointerdown', onPointerDown)
  }, [])

  return (
    <div
      ref={ref}
      data-testid="replay-chooser"
      role="dialog"
      aria-label={title}
      className="wh-card"
      style={{
        position: 'absolute',
        right: 0,
        bottom: 'calc(100% + 10px)',
        padding: '12px 14px 14px',
        minWidth: 300,
        maxWidth: 'calc(100vw - 24px)',
        zIndex: 20,
        boxShadow: '0 8px 24px rgba(0,0,0,0.45)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <button
          type="button"
          aria-label={t('controls.closeChooser')}
          onClick={onClose}
          className="wh-btn-icon"
          style={{ width: 26, height: 26 }}
        >
          <Icon name="close" size={15} />
        </button>
        <span style={{ fontWeight: 700, fontSize: 15 }}>{title}</span>
      </div>

      <div
        style={{
          display: 'flex',
          gap: 2,
          background: COLORS.surfaceContainerHigh,
          borderRadius: 8,
          padding: 3,
        }}
      >
        {options.map((option) => {
          const active = option.value === selected
          return (
            <button
              key={option.value}
              type="button"
              aria-pressed={active}
              onClick={() => {
                onSelect(option.value)
                onClose()
              }}
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                padding: '8px 6px',
                border: 'none',
                borderRadius: 6,
                background: active ? '#fff' : 'transparent',
                boxShadow: active ? '0 1px 3px rgba(0,0,0,0.18)' : undefined,
                color: COLORS.onSurface,
                cursor: 'pointer',
              }}
            >
              <span style={{ fontSize: 14, fontWeight: 700 }}>{option.top}</span>
              {option.bottom !== undefined && (
                <span
                  style={{
                    fontSize: 9,
                    fontWeight: 700,
                    letterSpacing: 0.4,
                    textTransform: 'uppercase',
                  }}
                >
                  {option.bottom}
                </span>
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}
